import { Fragment, useEffect, useState } from 'react'
import moment from 'moment'
import trim from 'lodash/trim'
import { Dialog, Transition } from '@headlessui/react'
import { TrashIcon, XIcon } from '@heroicons/react/outline'
import apiGet from 'lib/network/apiGet'
import apiPost from 'lib/network/apiPost'
import apiDelete from 'lib/network/apiDelete'

export const PreBookingNotes = ({ intentId, t }) => {
  const [notes, setNotes] = useState([])
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setNotes([])
    apiGet(`pre-bookings/${intentId}/notes`).then(res => {
      if (res?.success) setNotes(res.data.notes || [])
    })
  }, [intentId])

  const addNote = async () => {
    if (!trim(draft)) return
    setSaving(true)
    try {
      const res = await apiPost(`pre-bookings/${intentId}/notes`, { note: trim(draft) })
      if (res?.success) {
        setNotes(res.data.notes || [])
        setDraft('')
      }
    } finally {
      setSaving(false)
    }
  }

  const deleteNote = async noteId => {
    const res = await apiDelete(`pre-bookings/${intentId}/notes/${noteId}`)
    if (res?.success) setNotes(res.data.notes || [])
  }

  return (
    <div className="space-y-3">
      <textarea
        value={draft}
        onChange={e => setDraft(e.target.value)}
        rows={3}
        maxLength={2000}
        placeholder={t('preBookings.notes.placeholder')}
        className="w-full resize-none rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
      />
      <button
        onClick={addNote}
        disabled={saving || !trim(draft)}
        className="inline-flex items-center rounded-md bg-dark-600 px-3 py-2 text-xs font-semibold text-white hover:bg-dark-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {t('preBookings.notes.add')}
      </button>
      {notes.length === 0 ? (
        <p className="text-sm text-gray-500">{t('preBookings.notes.empty')}</p>
      ) : (
        [...notes].reverse().map(note => (
          <div key={note._id} className="rounded-md border border-gray-200 bg-gray-50 p-3">
            <div className="mb-1 flex items-center justify-between gap-3">
              <span className="text-xs font-medium text-gray-700">
                {note.addedBy?.fullName || '-'} · <span className="font-normal text-gray-500">{moment(note.createdAt).format('DD MMM YYYY · HH:mm')}</span>
              </span>
              <button onClick={() => deleteNote(note._id)} title={t('preBookings.notes.delete')} className="p-1 text-gray-400 hover:text-red-500">
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
            <p className="whitespace-pre-wrap text-sm text-gray-800">{note.note}</p>
          </div>
        ))
      )}
    </div>
  )
}

export const PreBookingNotesDrawer = ({ intent, onClose, t }) => (
  <Transition.Root show={Boolean(intent)} as={Fragment}>
    <Dialog as="div" className="fixed inset-0 z-50 overflow-hidden" onClose={onClose}>
      <Transition.Child
        as={Fragment}
        enter="ease-in-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in-out duration-300"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <Dialog.Overlay className="absolute inset-0 bg-gray-900 bg-opacity-75" />
      </Transition.Child>
      <div className="fixed inset-y-0 right-0 flex max-w-full">
        <Transition.Child
          as={Fragment}
          enter="transform transition ease-in-out duration-300"
          enterFrom="translate-x-full"
          enterTo="translate-x-0"
          leave="transform transition ease-in-out duration-300"
          leaveFrom="translate-x-0"
          leaveTo="translate-x-full"
        >
          <div className="flex h-full w-screen max-w-lg flex-col bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-3 bg-dark-600 px-6 py-5">
              <div className="min-w-0">
                <Dialog.Title className="text-lg font-semibold text-white">{t('preBookings.sections.notes')}</Dialog.Title>
                <p className="mt-0.5 text-xs text-gray-300">{t('preBookings.notes.description')}</p>
              </div>
              <button onClick={onClose} className="rounded-full p-1 text-white hover:bg-white hover:bg-opacity-10">
                <XIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">{intent ? <PreBookingNotes intentId={intent._id} t={t} /> : null}</div>
          </div>
        </Transition.Child>
      </div>
    </Dialog>
  </Transition.Root>
)
